import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common";
import { Sparkles, AlertTriangle, History as HistoryIcon, FlaskConical, Plus, Trash2,
        GraduationCap, BarChart3, Pencil, Check, X, GitCompareArrows,
        Download, FileJson, FileSpreadsheet, FileText, HardDriveUpload,
        FolderOpen, ExternalLink, Zap, Play, ListChecks } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  published: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  incomplete: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
};

// ---------------------------------------------------------------------------
// Weight editor — shared by Lead Score / Closing Probability / Effort /
// Quality weight groups. Live sum indicator so an admin sees the 100%
// validation the backend enforces BEFORE hitting Publish and getting a 422.
// ---------------------------------------------------------------------------
const WeightEditor = ({ label, weights, onChange, testId }) => {
  const entries = Object.entries(weights || {});
  const sum = entries.reduce((s, [, v]) => s + (Number(v) || 0), 0);
  const setWeight = (k, v) => onChange({ ...weights, [k]: Number(v) });
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium">{label}</p>
        <Badge className={Math.abs(sum - 100) < 0.5 ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-red-500/15 text-red-600 dark:text-red-400"}>
          Sum: {sum.toFixed(1)}%
        </Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {entries.map(([k, v]) => (
          <div key={k} className="space-y-0.5">
            <Label className="text-[10px] text-muted-foreground capitalize">{k.replaceAll("_", " ")}</Label>
            <Input type="number" className="h-7 text-xs" value={v} onChange={(e) => setWeight(k, e.target.value)} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Generic draft/publish/version-history mechanics — reused by every scope
// (global, per-product profile, consultant). Content rendering is a render
// prop so each scope presents its own tailored editor while sharing the
// exact same publish/rollback flow and RBAC gating.
// ---------------------------------------------------------------------------
const ConfigEditor = ({ configId, renderContent }) => {
  const { hasCap } = useAuth();
  const [data, setData] = useState(undefined);
  const [content, setContent] = useState(null);
  const [changeNote, setChangeNote] = useState("");
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/ai/admin/configs/${configId}`).then((r) => {
      setData(r.data);
      setContent(r.data.draft?.content || {});
    }).catch(() => setData(null));
  };
  useEffect(() => { load(); }, [configId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVersions = () => {
    api.get(`/ai/admin/configs/${configId}/versions`).then((r) => setVersions(r.data)).catch(() => setVersions([]));
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/ai/admin/configs/${configId}/draft`, { content, change_note: changeNote || null });
      toast.success("Draft saved — production is unaffected until you Publish");
      setChangeNote("");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/ai/admin/configs/${configId}/publish`);
      toast.success("Published — now live for new AI generations");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const rollback = async (version) => {
    if (!window.confirm(`Roll back to v${version}? This publishes a new version with that content.`)) return;
    setBusy(true);
    try {
      await api.post(`/ai/admin/configs/${configId}/rollback`, { version });
      toast.success(`Rolled back to v${version}`);
      load(); loadVersions();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  if (data === undefined) return <p className="text-xs text-muted-foreground">Loading…</p>;
  if (!data) return <EmptyState icon={AlertTriangle} title="Config not found" hint="" />;

  return (
    <div className="space-y-3" data-testid={`ai-config-editor-${configId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={STATUS_TONE[data.status] || ""}>{data.status === "published" ? "Published" : "Incomplete"}</Badge>
        {data.published && <span className="text-xs text-muted-foreground">Live version: v{data.published.version}</span>}
        <span className="text-xs text-muted-foreground">Draft: v{data.config.draft_version}</span>
        <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto" onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadVersions(); }} data-testid={`ai-config-history-toggle-${configId}`}>
          <HistoryIcon className="h-3.5 w-3.5 mr-1" />{showHistory ? "Hide" : "Show"} version history
        </Button>
      </div>

      {showHistory && (
        <Card><CardContent className="pt-3 space-y-1.5">
          {versions.map((v) => (
            <div key={v.version_id} className="flex items-center gap-2 text-xs" data-testid={`ai-config-version-${v.version}`}>
              <Badge variant="outline" className="text-[9px]">v{v.version}</Badge>
              <span className="capitalize">{v.state}</span>
              {configId === "consultant" && (v.content?.quality_config_schema_version ?? 1) < 2
                && Object.keys(v.content?.quality_weights || {}).length > 0 && (
                <Badge variant="outline" className="text-[9px]">Legacy methodology</Badge>
              )}
              <span className="text-muted-foreground">{v.change_note || ""}</span>
              <span className="text-muted-foreground ml-auto">{v.published_at ? `Published ${fmtIST(v.published_at)}` : `Created ${fmtIST(v.created_at)}`}</span>
              {hasCap("ai.config.publish") && v.state !== "draft" && (
                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => rollback(v.version)}>Roll back to this</Button>
              )}
            </div>
          ))}
        </CardContent></Card>
      )}

      {content && renderContent(content, setContent)}

      {hasCap("ai.config.edit_draft") && (
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
          <Input placeholder="Change note (optional)" className="h-8 text-xs w-64" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
          <Button size="sm" variant="outline" disabled={busy} onClick={saveDraft} data-testid={`ai-config-save-draft-${configId}`}>Save draft</Button>
          {hasCap("ai.config.publish") && (
            <Button size="sm" disabled={busy} onClick={publish} data-testid={`ai-config-publish-${configId}`}>Publish</Button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Global Rules
// ---------------------------------------------------------------------------
const GlobalRulesContent = (content, setContent) => {
  const rules = content.rules || [];
  const setRule = (i, v) => setContent({ ...content, rules: rules.map((r, idx) => (idx === i ? v : r)) });
  const addRule = () => setContent({ ...content, rules: [...rules, ""] });
  const removeRule = (i) => setContent({ ...content, rules: rules.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-2">
      <Label className="text-xs">Global instructions (applied to every product)</Label>
      {rules.map((r, i) => (
        <div key={i} className="flex gap-2 items-start">
          <Textarea className="text-sm min-h-14" value={r} onChange={(e) => setRule(i, e.target.value)} />
          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => removeRule(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={addRule}><Plus className="h-3.5 w-3.5 mr-1" />Add rule</Button>
    </div>
  );
};

const GlobalRulesTab = () => (
  <Card><CardContent className="pt-4"><ConfigEditor configId="global" renderContent={GlobalRulesContent} /></CardContent></Card>
);

// ---------------------------------------------------------------------------
// Product Profiles
// ---------------------------------------------------------------------------
const ProductProfileContent = (content, setContent) => (
  <div className="space-y-4">
    <WeightEditor label="Lead Score weights" weights={content.lead_score_weights || {}}
      onChange={(w) => setContent({ ...content, lead_score_weights: w })} testId="lead-score-weights" />
    <WeightEditor label="Closing Probability weights" weights={content.closing_probability_weights || {}}
      onChange={(w) => setContent({ ...content, closing_probability_weights: w })} testId="closing-probability-weights" />
    <div className="space-y-1">
      <Label className="text-xs">Everything else (factor guidance, labels, guardrails, Temperature thresholds, output fields, summary instructions) — raw JSON</Label>
      <RawJsonEditor value={content} onChange={setContent} exclude={["lead_score_weights", "closing_probability_weights"]} />
    </div>
  </div>
);

const RawJsonEditor = ({ value, onChange, exclude = [] }) => {
  const visible = Object.fromEntries(Object.entries(value).filter(([k]) => !exclude.includes(k)));
  const [text, setText] = useState(JSON.stringify(visible, null, 2));
  const [error, setError] = useState(null);
  useEffect(() => { setText(JSON.stringify(visible, null, 2)); }, [JSON.stringify(visible)]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = () => {
    try {
      const parsed = JSON.parse(text);
      const preserved = Object.fromEntries(Object.entries(value).filter(([k]) => exclude.includes(k)));
      onChange({ ...preserved, ...parsed });
      setError(null);
      toast.success("Applied to draft (not yet saved — click Save draft below)");
    } catch (e) { setError(e.message); }
  };
  return (
    <div className="space-y-1">
      <Textarea className="font-mono text-xs min-h-56" value={text} onChange={(e) => setText(e.target.value)} />
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={apply}>Apply JSON</Button>
    </div>
  );
};

const ProductProfilesTab = () => {
  const [configs, setConfigs] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    api.get("/ai/admin/configs?scope_type=product").then((r) => setConfigs(r.data)).catch(() => setConfigs([]));
  }, []);
  if (configs === null) return <p className="text-xs text-muted-foreground">Loading…</p>;
  return (
    <div className="grid md:grid-cols-3 gap-3">
      <Card className="md:col-span-1"><CardContent className="pt-4 space-y-1.5">
        <p className="text-xs font-medium mb-1">Products & families</p>
        {configs.map((c) => (
          <div key={c.config_id}
            className={`flex items-center justify-between gap-2 rounded px-2 py-1.5 cursor-pointer text-sm ${selected === c.config_id ? "bg-accent" : "hover:bg-accent/50"}`}
            onClick={() => setSelected(c.config_id)} data-testid={`ai-product-row-${c.scope_id}`}>
            <span>{c.product_name || c.scope_id} {c.is_family && <Badge variant="outline" className="text-[8px] ml-1">Family</Badge>}</span>
            <Badge className={STATUS_TONE[c.status] || ""}>{c.status === "published" ? "OK" : "Incomplete"}</Badge>
          </div>
        ))}
      </CardContent></Card>
      <Card className="md:col-span-2"><CardContent className="pt-4">
        {selected ? <ConfigEditor configId={selected} renderContent={ProductProfileContent} />
          : <p className="text-xs text-muted-foreground">Select a product to configure its AI profile.</p>}
      </CardContent></Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Consultant Intelligence
// ---------------------------------------------------------------------------
// P5 correction, P6-completion correction round 2 — must match
// consultant_intelligence.QUALITY_FACTOR_KEYS exactly. lead_quality_
// adjusted_closure / conversion_consistency / closing_probability_
// improvement measure COMMERCIAL outcome, not consultation quality —
// moved wholesale to Commercial Results (P5B.2 Part 6) and are never
// editable here as if they still affected the Quality score. ethical_
// selling / support_complaints are REMOVED entirely (round 2, blocker 3)
// — neither is reducible to persisted facts / structurally always None
// — and are no longer editable as numeric weights at all (ethical_
// selling now renders as an advisory state, never a weight). compute_
// quality_score's own defensive filter already ignores anything outside
// this set regardless of what a stale stored document still contains;
// this keeps the UI honest about the same fact.
const QUALITY_FACTOR_KEYS = ["feedback_documentation_substance", "customer_understanding",
  "objection_discovery", "objection_handling", "customer_alignment", "followup_planning"];

const ConsultantContent = (content, setContent) => {
  const qualityWeights = content.quality_weights || {};
  const validQualityWeights = Object.fromEntries(
    Object.entries(qualityWeights).filter(([k]) => QUALITY_FACTOR_KEYS.includes(k)));
  const deprecatedKeys = Object.keys(qualityWeights).filter((k) => !QUALITY_FACTOR_KEYS.includes(k));
  return (
    <div className="space-y-4">
      <WeightEditor label="Consultant Effort Score weights" weights={content.effort_weights || {}}
        onChange={(w) => setContent({ ...content, effort_weights: w })} testId="effort-weights" />
      <WeightEditor label="Consultant Quality Score weights" weights={validQualityWeights}
        onChange={(w) => setContent({ ...content, quality_weights: w })} testId="quality-weights" />
      {deprecatedKeys.length > 0 && (
        <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
          data-testid="quality-weights-deprecated-notice">
          <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
          {deprecatedKeys.length} legacy key{deprecatedKeys.length > 1 ? "s" : ""} still stored in this draft
          ({deprecatedKeys.join(", ")}) — {deprecatedKeys.length > 1 ? "these" : "this"} no longer {deprecatedKeys.length > 1 ? "affect" : "affects"}
          the numeric Quality score (either moved to Commercial Results, or — for ethical_selling/
          support_complaints — moved to a separate advisory-only display that is never added to or
          subtracted from the score). Not shown above as editable Quality weights; saving this draft
          with any change removes {deprecatedKeys.length > 1 ? "them" : "it"} from the stored
          configuration. Prior published versions that used {deprecatedKeys.length > 1 ? "them" : "it"}
          remain fully visible in Version History, labeled Legacy.
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Thresholds & other settings — raw JSON</Label>
        <RawJsonEditor value={content} onChange={setContent} exclude={["effort_weights", "quality_weights"]} />
      </div>
    </div>
  );
};

const ConsultantIntelligenceTab = () => (
  <Card><CardContent className="pt-4"><ConfigEditor configId="consultant" renderContent={ConsultantContent} /></CardContent></Card>
);

// ---------------------------------------------------------------------------
// Test & Preview
// ---------------------------------------------------------------------------
const TestPreviewTab = () => {
  const [products, setProducts] = useState([]);
  const [productCode, setProductCode] = useState("");
  const [sample, setSample] = useState("");
  const [daysSinceResponse, setDaysSinceResponse] = useState("");
  const [priorAttempts, setPriorAttempts] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/ai/admin/test-runs").then((r) => setRuns(r.data)).catch(() => {});
  }, []);

  const run = async () => {
    if (!sample.trim()) { toast.error("Enter a sample scenario first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/admin/test-run", {
        product_code: productCode || null, sample_evidence: sample,
        days_since_last_response: daysSinceResponse === "" ? null : Number(daysSinceResponse),
        prior_interaction_count: priorAttempts === "" ? 0 : Number(priorAttempts),
      });
      setResult(data);
      setRuns((prev) => [data, ...prev].slice(0, 20));
      toast.success("Test run complete — no real customer data was touched");
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Card><CardContent className="pt-4 space-y-2">
        <p className="text-xs font-medium flex items-center gap-1.5"><FlaskConical className="h-3.5 w-3.5" />Test scenario</p>
        <p className="text-[11px] text-muted-foreground">Never creates or modifies a real customer, never writes a production AI snapshot — only this test-run log.</p>
        <Select value={productCode} onValueChange={setProductCode}>
          <SelectTrigger data-testid="ai-test-product" className="h-8 text-xs"><SelectValue placeholder="Product (optional)" /></SelectTrigger>
          <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea data-testid="ai-test-sample" className="min-h-40 text-sm" placeholder="Describe a sample customer scenario — e.g. '34-year-old software professional, strong profile, budget unclear, attended consultation, asked about skills assessment timeline...'" value={sample} onChange={(e) => setSample(e.target.value)} />
        <div className="rounded-md border border-dashed p-2 space-y-1.5">
          <p className="text-[11px] text-muted-foreground">
            Optional — CRM-derived signals no LLM ever infers from text (for a real customer these come from actual interaction history). Leave blank to simulate "no data yet"; fill in to test no-response/ghosting behaviour.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px]">Days since customer's last response</Label>
              <Input data-testid="ai-test-days-no-response" type="number" min="0" className="h-7 text-xs"
                value={daysSinceResponse} onChange={(e) => setDaysSinceResponse(e.target.value)} placeholder="e.g. 10" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">Prior follow-up attempts</Label>
              <Input data-testid="ai-test-prior-attempts" type="number" min="0" className="h-7 text-xs"
                value={priorAttempts} onChange={(e) => setPriorAttempts(e.target.value)} placeholder="e.g. 4" />
            </div>
          </div>
        </div>
        <Button size="sm" disabled={busy} onClick={run} data-testid="ai-test-run-button">{busy ? "Running…" : "Run test"}</Button>
      </CardContent></Card>
      <Card><CardContent className="pt-4 space-y-2">
        <p className="text-xs font-medium">Result</p>
        {!result ? <p className="text-xs text-muted-foreground">Run a test to see the structured output here.</p> : (
          <div className="space-y-2 text-sm" data-testid="ai-test-result">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <span>Lead Score: <b>{result.output.lead_score?.value != null ? Math.round(result.output.lead_score.value) : "—"}</b>
                {result.output.lead_score?.confidence && <span className="text-muted-foreground"> ({result.output.lead_score.confidence}, cov {Math.round((result.output.lead_score.coverage || 0) * 100)}%)</span>}</span>
              <span>Closing: <b>{result.output.closing_probability?.value != null ? Math.round(result.output.closing_probability.value) + "%" : result.output.closing_probability?.state}</b>
                {result.output.closing_probability?.capped_by && <span className="text-amber-600 dark:text-amber-400"> (capped: {result.output.closing_probability.capped_by})</span>}</span>
              <span>Temp: <b>{result.output.temperature}</b></span>
            </div>
            <p className="text-xs"><b>Background:</b> {result.output.narrative?.background}</p>
            <p className="text-xs"><b>Strategy:</b> {result.output.strategy?.recommended_approach}</p>
            {result.output.objections?.length > 0 && (
              <p className="text-xs"><b>Objections:</b> {result.output.objections.map((o) => o.text).join("; ")}</p>
            )}
            {result.output.risks?.length > 0 && (
              <p className="text-xs"><b>Risks:</b> {result.output.risks.map((r) => r.description).join("; ")}</p>
            )}
            {result.output.ghosting_simulation_note && (
              <p className="text-[11px] rounded bg-muted p-1.5" data-testid="ai-test-ghosting-note">{result.output.ghosting_simulation_note}</p>
            )}
            {result.output.applied_guardrails?.length > 0 && (
              <div className="text-xs" data-testid="ai-test-applied-guardrails">
                <b>Guardrails:</b>
                <ul className="mt-0.5 space-y-0.5">
                  {result.output.applied_guardrails.map((g) => (
                    <li key={g.condition_key} className="flex items-center gap-1.5">
                      <Badge variant="outline" className={"text-[9px] " + (g.applied ? "bg-red-500/15 text-red-600 dark:text-red-400" : g.active ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "text-muted-foreground")}>
                        {g.applied ? "APPLIED" : g.active ? "active, not lowest" : g.simulable_from_this_input ? "not active" : "not simulable from this input"}
                      </Badge>
                      <span>{g.label} (cap {g.cap})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <details className="text-xs" open>
              <summary className="cursor-pointer text-muted-foreground">
                Factor scores — Unknown factors are shown, not hidden (evidence coverage {Math.round((result.output.lead_score?.coverage || 0) * 100)}%)
              </summary>
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                {Object.entries(result.output.factor_assessments || {}).map(([k, v]) => (
                  <span key={k}>
                    {k}: {v?.score != null
                      ? <b>{v.score}</b>
                      : <b className="text-muted-foreground italic">Unknown</b>}
                  </span>
                ))}
              </div>
            </details>
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Closing Probability inputs used</summary>
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                {Object.entries(result.output.closing_factor_scores_used || {}).map(([k, v]) => (
                  <span key={k}>{k}: <b>{typeof v === "number" ? v.toFixed(2) : v}</b></span>
                ))}
              </div>
            </details>
          </div>
        )}
      </CardContent></Card>
      {runs.length > 0 && (
        <Card className="md:col-span-2"><CardContent className="pt-4">
          <p className="text-xs font-medium mb-2">Recent test runs</p>
          <div className="space-y-1">
            {runs.map((r) => (
              <div key={r.run_id} className="text-xs flex gap-2 items-center border-b pb-1">
                <span className="text-muted-foreground">{fmtIST(r.created_at)}</span>
                <span>{r.product_code || "—"}</span>
                <span className="truncate flex-1">{r.sample_input.slice(0, 80)}</span>
                <span>LS: {r.output.lead_score?.value != null ? Math.round(r.output.lead_score.value) : "—"}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Correction / training review queue (spec section 73) — an admin reviews
// a flagged AI classification and either applies it (as a suggested
// training example, appended to a chosen scope's DRAFT — still requires
// that scope's own explicit Publish before it affects anything real) or
// dismisses it. Nothing here ever changes production on its own.
// ---------------------------------------------------------------------------
const CorrectionsTab = () => {
  const [corrections, setCorrections] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [targets, setTargets] = useState({});

  const load = () => {
    api.get("/ai/admin/corrections?status=open").then((r) => setCorrections(r.data)).catch(() => setCorrections([]));
  };
  useEffect(() => {
    load();
    api.get("/ai/admin/configs?scope_type=product").then((r) => setConfigs(r.data)).catch(() => {});
  }, []);

  const apply = async (id) => {
    const target = targets[id];
    if (!target) { toast.error("Pick a product profile to apply this to first"); return; }
    try {
      await api.post(`/ai/admin/corrections/${id}/apply`, { target_config_id: target });
      toast.success("Applied as a suggested training example — still requires Publish on that profile to take effect");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const dismiss = async (id) => {
    try {
      await api.post(`/ai/admin/corrections/${id}/dismiss`);
      toast.success("Dismissed");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (corrections === null) return <p className="text-xs text-muted-foreground">Loading…</p>;
  if (corrections.length === 0) {
    return <EmptyState icon={Sparkles} title="No open corrections" hint="When a manager flags an AI classification as incorrect from Customer 360, it appears here for review." />;
  }
  return (
    <div className="space-y-2" data-testid="ai-corrections-queue">
      {corrections.map((c) => (
        <Card key={c.correction_id} data-testid={`ai-correction-${c.correction_id}`}>
          <CardContent className="pt-3 space-y-1.5 text-sm">
            <p className="text-xs text-muted-foreground">{c.field_path} · flagged {fmtIST(c.flagged_at)}</p>
            <p><span className="text-muted-foreground">AI said:</span> {String(c.ai_value ?? "—")}</p>
            <p><span className="text-muted-foreground">Corrected to:</span> {c.corrected_value}</p>
            <p className="text-xs text-muted-foreground">{c.explanation}</p>
            <div className="flex items-center gap-2 pt-1">
              <Select value={targets[c.correction_id] || ""} onValueChange={(v) => setTargets({ ...targets, [c.correction_id]: v })}>
                <SelectTrigger className="h-7 text-xs w-56"><SelectValue placeholder="Apply to which profile?" /></SelectTrigger>
                <SelectContent>{configs.map((cfg) => <SelectItem key={cfg.config_id} value={cfg.config_id}>{cfg.product_name || cfg.scope_id}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => apply(c.correction_id)} data-testid={`ai-correction-apply-${c.correction_id}`}>Apply to draft</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => dismiss(c.correction_id)} data-testid={`ai-correction-dismiss-${c.correction_id}`}>Dismiss</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AI Intelligence V3 Phase 5 — Continuous Learning + Governance. AI
// proposes, a human decides: every card below is a candidate lesson/change
// the weekly or monthly mining run found — nothing here is live until a
// reviewer explicitly approves it (kind-A → active ai_intelligence_memory,
// kind-B → the config DRAFT only, Publish still required separately).
// ---------------------------------------------------------------------------
const STRENGTH_TONE = {
  observational: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
  correlational: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  strong_pattern: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
};
const PROPOSAL_STATUS_TONE = {
  pending_review: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  approved: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  modified: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  rejected: "bg-red-500/15 text-red-600 dark:text-red-400",
};

const fmtDist = (dist) => {
  if (!dist || Object.keys(dist).length === 0) return "—";
  const total = Object.values(dist).reduce((s, v) => s + v, 0) || 1;
  return Object.entries(dist).map(([k, v]) => `${k.replaceAll("_", " ")}: ${v} (${Math.round(100 * v / total)}%)`).join(" · ");
};

const KindBDiff = ({ proposalId }) => {
  const [diff, setDiff] = useState(undefined);
  useEffect(() => {
    api.get(`/ai/admin/learning/proposals/${proposalId}/diff`).then((r) => setDiff(r.data)).catch((e) => setDiff({ error: errDetail(e) }));
  }, [proposalId]);
  if (diff === undefined) return <p className="text-xs text-muted-foreground">Loading diff…</p>;
  if (diff.error) return <p className="text-xs text-red-600 dark:text-red-400">{diff.error}</p>;
  return (
    <div className="space-y-2 text-xs" data-testid={`ai-proposal-diff-${proposalId}`}>
      {diff.stale && (
        <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> The config Draft changed since this proposal was created — review this fresh diff before approving.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        {Object.entries(diff.patch || {}).map(([key]) => (
          <div key={key} className="rounded border p-2 space-y-1">
            <p className="font-medium">{key.replaceAll("_", " ")}</p>
            <p className="text-muted-foreground">Before: {JSON.stringify(diff.before?.[key])}</p>
            <p>After: {JSON.stringify(diff.after?.[key])}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const LearningProposalCard = ({ proposal, onDecided }) => {
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(proposal.content || "");
  const [showDiff, setShowDiff] = useState(false);
  const [busy, setBusy] = useState(false);
  const isKindB = proposal.proposal_kind === "configuration_scoring";

  const approve = async (acknowledgeStale = false) => {
    setBusy(true);
    try {
      const body = {};
      if (editing && !isKindB) body.edited_content = editedContent;
      if (acknowledgeStale) body.acknowledge_stale = true;
      await api.post(`/ai/admin/learning/proposals/${proposal.proposal_id}/approve`, body);
      toast.success(editing ? "Approved with edits" : "Approved");
      onDecided();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("The config Draft changed since this proposal was created — review the diff, then approve again to confirm.");
        setShowDiff(true);
      } else {
        toast.error(errDetail(e));
      }
    } finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt("Optional reason (shown in the audit trail):") || null;
    setBusy(true);
    try {
      await api.post(`/ai/admin/learning/proposals/${proposal.proposal_id}/reject`, { reason });
      toast.success("Rejected");
      onDecided();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Card data-testid={`ai-learning-proposal-${proposal.proposal_id}`}>
      <CardContent className="pt-3 space-y-2 text-sm">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <p className="font-medium">{proposal.title}</p>
            <p className="text-[10px] text-muted-foreground">
              {proposal.scope} · {isKindB ? "Configuration/Scoring" : "Knowledge/Playbook"}
              {proposal.action && proposal.action !== "new" ? ` · ${proposal.action}` : ""} · {fmtIST(proposal.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge className={STRENGTH_TONE[proposal.evidence_strength] || ""}>{(proposal.evidence_strength || "").replaceAll("_", " ")}</Badge>
            <Badge className={PROPOSAL_STATUS_TONE[proposal.status] || ""}>{proposal.status.replaceAll("_", " ")}</Badge>
          </div>
        </div>

        <p className="text-xs"><span className="text-muted-foreground">Why the AI proposed this:</span> {proposal.reasoning}</p>
        <p className="text-xs"><span className="text-muted-foreground">Expected effect if approved:</span> {proposal.expected_effect}</p>

        <div className="rounded border p-2 bg-muted/30 text-xs space-y-0.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Observed evidence (backend-computed)</p>
          <p>Sample size: {proposal.evidence_snapshot?.sample_size ?? "—"}</p>
          <p>Outcome distribution: {fmtDist(proposal.evidence_snapshot?.outcome_distribution)}</p>
          {proposal.evidence_snapshot?.baseline_outcome_distribution && (
            <p className="text-muted-foreground">Baseline (whole dataset): {fmtDist(proposal.evidence_snapshot.baseline_outcome_distribution)}</p>
          )}
        </div>

        {editing && !isKindB ? (
          <Textarea className="text-xs" rows={3} value={editedContent} onChange={(e) => setEditedContent(e.target.value)} />
        ) : (
          <p className="text-xs"><span className="text-muted-foreground">Proposed {isKindB ? "change" : "lesson"}:</span> {proposal.content}</p>
        )}

        {proposal.supporting_examples?.length > 0 && (
          <div className="text-xs">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Supporting examples (anonymized)</p>
            <ul className="list-disc pl-4 space-y-0.5">{proposal.supporting_examples.map((ex, i) => <li key={i}>{ex}</li>)}</ul>
          </div>
        )}
        {proposal.counterexamples?.length > 0 && (
          <div className="text-xs">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Counterexamples / limitations</p>
            <ul className="list-disc pl-4 space-y-0.5">{proposal.counterexamples.map((ex, i) => <li key={i}>{ex}</li>)}</ul>
          </div>
        )}

        {isKindB && (
          <div>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5" onClick={() => setShowDiff(!showDiff)}>
              <GitCompareArrows className="h-3 w-3 mr-1" />{showDiff ? "Hide diff" : "View before/after diff"}
            </Button>
            {showDiff && <KindBDiff proposalId={proposal.proposal_id} />}
          </div>
        )}

        {proposal.status === "pending_review" && (
          <div className="flex items-center gap-1.5 pt-1">
            <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => approve(proposal.stale)}
              data-testid={`ai-learning-proposal-approve-${proposal.proposal_id}`}>
              <Check className="h-3.5 w-3.5 mr-1" />{proposal.stale ? "Approve (confirm fresh diff)" : "Approve as-is"}
            </Button>
            {!isKindB && (
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                onClick={() => editing ? approve() : setEditing(true)}>
                <Pencil className="h-3.5 w-3.5 mr-1" />{editing ? "Save & approve" : "Edit then approve"}
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={reject}
              data-testid={`ai-learning-proposal-reject-${proposal.proposal_id}`}>
              <X className="h-3.5 w-3.5 mr-1" />Reject
            </Button>
          </div>
        )}
        {proposal.status === "rejected" && proposal.rejection_reason && (
          <p className="text-[10px] text-muted-foreground">Rejected: {proposal.rejection_reason}</p>
        )}
      </CardContent>
    </Card>
  );
};

const LearningProposalsTab = () => {
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [proposals, setProposals] = useState(null);
  const [running, setRunning] = useState(null);

  const load = () => {
    setProposals(null);
    api.get(`/ai/admin/learning/proposals?status=${statusFilter}`).then((r) => setProposals(r.data)).catch(() => setProposals([]));
  };
  useEffect(load, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const runNow = async (period) => {
    setRunning(period);
    try {
      const r = await api.post("/ai/admin/learning/run", { period });
      toast.success(r.data.ran ? `Run complete — ${r.data.proposals_created} proposal(s) created` : `Nothing new to analyze — ${r.data.reason}`);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setRunning(null); }
  };

  return (
    <div className="space-y-3" data-testid="ai-learning-proposals-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          {["pending_review", "approved", "rejected"].map((s) => (
            <Button key={s} size="sm" variant={statusFilter === s ? "default" : "outline"} className="h-7 text-xs"
              onClick={() => setStatusFilter(s)} data-testid={`ai-learning-proposals-filter-${s}`}>
              {s.replaceAll("_", " ")}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!running} onClick={() => runNow("weekly")}>
            {running === "weekly" ? "Running…" : "Run weekly analysis now"}
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!running} onClick={() => runNow("monthly")}>
            {running === "monthly" ? "Running…" : "Run monthly analysis now"}
          </Button>
        </div>
      </div>
      {proposals === null ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : proposals.length === 0 ? (
        <EmptyState icon={GraduationCap} title={`No ${statusFilter.replaceAll("_", " ")} proposals`}
          hint="The weekly and monthly pattern-mining runs propose lessons/changes here as real sales evidence accumulates — nothing is ever applied automatically." />
      ) : (
        <div className="space-y-2">
          {proposals.map((p) => <LearningProposalCard key={p.proposal_id} proposal={p} onDecided={load} />)}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AI Intelligence V3 Phase 7 — Intelligence Memory Portability / Google
// Drive. Read-only: this tab only ever GETs/POSTs against the export
// endpoints, which touch nothing but ai_intelligence_memory read-only and
// never call an LLM. Reuses the exact same connected Google Drive
// integration Admin → Integrations → Backups already uses.
// ---------------------------------------------------------------------------
const FILTER_OPTIONS = [["all", "All history"], ["active", "Active only"], ["inactive", "Inactive/superseded"]];

const MemoryExportDriveFolder = ({ onSaved }) => {
  const [folder, setFolder] = useState(null); // {folder_id, folder_name, folder_link, connection_status, is_explicit}
  const [input, setInput] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/ai/admin/learning/memory/export/drive-folder")
      .then((r) => { setFolder(r.data); setEditing(!r.data.folder_id); })
      .catch(() => setFolder({ folder_id: null, connection_status: "not_connected" }));
  };
  useEffect(load, []);

  const save = async () => {
    if (!input.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/admin/learning/memory/export/drive-folder", { folder: input.trim() });
      toast.success(`Memory Export destination set to "${data.folder_name}"`);
      setInput("");
      setEditing(false);
      load();
      onSaved?.();
    } catch (e) { toast.error(errDetail(e, "Couldn't verify that Drive folder")); }
    finally { setBusy(false); }
  };

  if (folder === null) return <p className="text-xs text-muted-foreground">Loading destination…</p>;

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="ai-memory-export-drive-folder">
      <p className="text-xs font-medium flex items-center gap-1.5"><FolderOpen className="h-3.5 w-3.5" />Google Drive folder</p>
      {folder.folder_id && !editing ? (
        <div className="space-y-1.5">
          <p className="text-xs">
            Exports upload to: <span className="font-medium">{folder.folder_name || folder.folder_id}</span>
            {folder.connection_status === "not_connected" && (
              <span className="text-amber-600 dark:text-amber-400"> — Google is disconnected; this destination is saved and will be used again once reconnected.</span>
            )}
          </p>
          <div className="flex items-center gap-1.5">
            <a href={folder.folder_link} target="_blank" rel="noreferrer" data-testid="ai-memory-export-open-folder">
              <Button size="sm" variant="outline" className="h-7 text-xs"><ExternalLink className="h-3 w-3 mr-1" />Open Folder in Google Drive</Button>
            </a>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditing(true); setInput(""); }}>Change folder</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          {!folder.folder_id && (
            <p className="text-xs text-amber-600 dark:text-amber-400">Google Drive destination not configured — paste a folder link below.</p>
          )}
          <div className="flex items-center gap-1.5">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Paste Google Drive folder link"
              className="h-8 text-xs max-w-sm" data-testid="ai-memory-export-drive-folder-input" />
            <Button size="sm" className="h-8 text-xs" disabled={busy || !input.trim()} onClick={save}
              data-testid="ai-memory-export-drive-folder-save">
              {busy ? "Saving…" : "Save"}
            </Button>
            {folder.folder_id && (
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MemoryExportTab = () => {
  const [filter, setFilter] = useState("all");
  const [counts, setCounts] = useState(null);
  const [driveBusy, setDriveBusy] = useState(false);
  const [lastExport, setLastExport] = useState(null); // {filename, drive_link, drive_folder_link, record_count}
  const [folderConfigured, setFolderConfigured] = useState(null);

  const activeParam = filter === "active" ? "true" : filter === "inactive" ? "false" : "";
  const loadCounts = () => {
    api.get(`/ai/admin/learning/memory/export?format=json${activeParam ? `&active=${activeParam}` : ""}`)
      .then((r) => setCounts(r.data)).catch(() => setCounts(null));
  };
  useEffect(loadCounts, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const checkFolderConfigured = () => {
    api.get("/ai/admin/learning/memory/export/drive-folder")
      .then((r) => setFolderConfigured(!!r.data.folder_id)).catch(() => setFolderConfigured(false));
  };
  useEffect(checkFolderConfigured, []);

  const downloadUrl = (format) =>
    `${backend}/api/ai/admin/learning/memory/export?format=${format}${activeParam ? `&active=${activeParam}` : ""}`;

  const exportToDrive = async (format) => {
    setDriveBusy(format);
    setLastExport(null);
    try {
      const body = { format };
      if (activeParam) body.active = activeParam === "true";
      const { data } = await api.post("/ai/admin/learning/memory/export/drive", body);
      toast.success(`Uploaded "${data.filename}" to Google Drive (${data.record_count} record(s))`);
      setLastExport(data);
    } catch (e) { toast.error(errDetail(e, "Google Drive export failed")); } finally { setDriveBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="ai-memory-export-tab">
      <p className="text-sm text-muted-foreground">
        Export Aussie Pathways' approved AI Intelligence Memory — the company's own learned sales
        intelligence, independent of any one AI provider or model. Pending/rejected proposals are
        never included; only human-approved memory.
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        {FILTER_OPTIONS.map(([v, l]) => (
          <Button key={v} size="sm" variant={filter === v ? "default" : "outline"} className="h-7 text-xs"
            onClick={() => setFilter(v)} data-testid={`ai-memory-export-filter-${v}`}>{l}</Button>
        ))}
      </div>
      {counts && (
        <div className="grid grid-cols-3 gap-2 max-w-md">
          {[["Total records", counts.record_count], ["Active", counts.active_count], ["Inactive/superseded", counts.inactive_count]].map(([label, value]) => (
            <div key={label} className="rounded-md border p-2 text-center">
              <p className="text-lg font-semibold tabular-nums">{value}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{label}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap pt-1">
        <a href={downloadUrl("json")} target="_blank" rel="noreferrer" data-testid="ai-memory-export-json">
          <Button size="sm" variant="outline" className="h-8 text-xs"><FileJson className="h-3.5 w-3.5 mr-1.5" />Export JSON</Button>
        </a>
        <a href={downloadUrl("csv")} target="_blank" rel="noreferrer" data-testid="ai-memory-export-csv">
          <Button size="sm" variant="outline" className="h-8 text-xs"><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />Export CSV</Button>
        </a>
        <a href={downloadUrl("markdown")} target="_blank" rel="noreferrer" data-testid="ai-memory-export-markdown">
          <Button size="sm" variant="outline" className="h-8 text-xs"><FileText className="h-3.5 w-3.5 mr-1.5" />Export Markdown</Button>
        </a>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!driveBusy || folderConfigured === false}
          onClick={() => exportToDrive("json")} data-testid="ai-memory-export-drive"
          title={folderConfigured === false ? "Configure a Google Drive folder below first" : undefined}>
          <HardDriveUpload className="h-3.5 w-3.5 mr-1.5" />{driveBusy ? "Uploading…" : "Export to Google Drive"}
        </Button>
      </div>
      {lastExport && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="ai-memory-export-last-result">
          {lastExport.drive_link && (
            <a href={lastExport.drive_link} target="_blank" rel="noreferrer" data-testid="ai-memory-export-open-file">
              <Button size="sm" variant="outline" className="h-7 text-xs"><ExternalLink className="h-3 w-3 mr-1" />Open Export in Google Drive</Button>
            </a>
          )}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">
        <Download className="h-3 w-3 inline mr-1" />Downloads happen directly in your browser, regardless
        of Drive connectivity. Google Drive export reuses the same connected Google Workspace account as
        database backups (Administration → Integrations) — connect it there first if needed.
      </p>
      <MemoryExportDriveFolder onSaved={checkFolderConfigured} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// AI Usage / Cost Visibility (2026-09) — real running-cost dashboard over
// the SAME ai_call_telemetry every AI-calling module already writes to (no
// second telemetry system). Cost is never recomputed client-side from a
// "current" price — every row already carries the estimated_cost that was
// computed server-side, once, against whatever price was actually in
// effect at call time (ai_learning.record_call_telemetry) — the frontend
// only ever sums/displays what the backend already decided.
// ---------------------------------------------------------------------------
const AI_CALL_TYPE_LABELS = {
  v2_factor_extraction: "V2 scoring extraction",
  v2_factor_extraction_test: "V2 scoring extraction (Test & Preview)",
  mentor_briefing: "Sales Mentor",
  evidence_extraction: "Evidence extraction",
  learning_analysis_weekly: "Learning analysis (weekly)",
  learning_analysis_monthly: "Learning analysis (monthly)",
  consultant_quality_extraction: "Consultant Quality",
  consultant_coaching: "Consultant Coaching",
  legacy_summary: "Legacy — Summary",
  legacy_intent: "Legacy — Intent",
  legacy_report_summary: "Legacy — Report summary",
};

const fmtMoney = (v) => (v == null ? "—" : `$${v < 0.01 && v > 0 ? v.toFixed(6) : v.toFixed(2)}`);
const fmtTok = (v) => (v ?? 0).toLocaleString();

const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "month", label: "This month" },
  { key: "prev_month", label: "Previous month" },
  { key: "custom", label: "Custom" },
];

function presetToRange(preset, customFrom, customTo) {
  const now = new Date();
  if (preset === "today") {
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { date_from: from.toISOString(), date_to: null };
  }
  if (preset === "7d") {
    const from = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
    return { date_from: from.toISOString(), date_to: null };
  }
  if (preset === "month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { date_from: from.toISOString(), date_to: null };
  }
  if (preset === "prev_month") {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 1);
    return { date_from: from.toISOString(), date_to: to.toISOString() };
  }
  return { date_from: customFrom || null, date_to: customTo || null };
}

const UsageBucketTable = ({ title, buckets, labelFor }) => {
  const rows = Object.entries(buckets || {}).sort((a, b) => b[1].calls_total - a[1].calls_total);
  return (
    <div className="rounded-md border overflow-x-auto">
      <p className="text-xs font-medium p-2 border-b bg-muted/30">{title}</p>
      <table className="w-full text-xs">
        <thead className="bg-muted/50"><tr>
          <th className="text-left p-2">{title === "By AI function" ? "Function" : title === "By model" ? "Model" : "Category"}</th>
          <th className="text-right p-2">Calls</th><th className="text-right p-2">Failed</th>
          <th className="text-right p-2">Input</th><th className="text-right p-2">Cached</th>
          <th className="text-right p-2">Output</th><th className="text-right p-2">Total tok</th>
          <th className="text-right p-2">Est. cost</th><th className="text-right p-2">Avg/call</th>
        </tr></thead>
        <tbody>
          {rows.map(([key, b]) => (
            <tr key={key} className="border-t">
              <td className="p-2">{labelFor ? labelFor(key) : key.replaceAll("_", " ")}</td>
              <td className="p-2 text-right tabular-nums">{b.calls_total}</td>
              <td className="p-2 text-right tabular-nums">{b.calls_failed}</td>
              <td className="p-2 text-right tabular-nums">{fmtTok(b.prompt_tokens)}</td>
              <td className="p-2 text-right tabular-nums">{fmtTok(b.cached_tokens)}</td>
              <td className="p-2 text-right tabular-nums">{fmtTok(b.completion_tokens)}</td>
              <td className="p-2 text-right tabular-nums">{fmtTok(b.total_tokens)}</td>
              <td className="p-2 text-right tabular-nums">
                {fmtMoney(b.estimated_cost)}
                {b.priced_calls < b.calls_total && b.calls_total > 0 && (
                  <span className="text-muted-foreground"> ({b.priced_calls}/{b.calls_total} priced)</span>
                )}
              </td>
              <td className="p-2 text-right tabular-nums">{fmtMoney(b.avg_cost_per_call)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="p-3 text-center text-muted-foreground">No calls in this range.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Failed-call diagnostics (2026-09 Sales Mentor / GPT-4o audit) — per-call
// drill-down over the SAME ai_call_telemetry rows the summary above already
// aggregates (GET /ai/admin/telemetry/calls?success=false), now carrying the
// six new diagnostic fields ai_learning.record_call_telemetry writes on
// every call (failure_stage, provider_http_status, provider_request_id,
// finish_reason, reasoning_tokens, requested_model) — never exposes the API
// key or raw customer content, only the already-sanitized error string and
// opportunity/contact IDs the summary view already surfaces.
// ---------------------------------------------------------------------------
const FAILURE_STAGE_LABELS = {
  preflight: "Preflight (no model configured)",
  provider: "Provider (real OpenAI API error)",
  structured_output_parse: "Structured-output parse (model returned no usable JSON)",
  validation: "Validation (response didn't match the expected schema)",
  persistence: "Persistence (DB write after a successful call)",
  unknown: "Unknown",
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const FailedCallRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t cursor-pointer hover:bg-muted/30" onClick={() => setOpen((o) => !o)}
        data-testid={`ai-failed-call-row-${row.telemetry_id}`}>
        <td className="p-2">{open ? "▾" : "▸"} {AI_CALL_TYPE_LABELS[row.call_type] || row.call_type}</td>
        <td className="p-2">{row.is_automatic ? "Automatic" : "Manual"}</td>
        <td className="p-2">{row.requested_model || row.model || "—"}</td>
        <td className="p-2">{FAILURE_STAGE_LABELS[row.failure_stage] || row.failure_stage || "—"}</td>
        <td className="p-2 whitespace-nowrap">{fmtWhen(row.at)}</td>
      </tr>
      {open && (
        <tr className="border-t bg-muted/20">
          <td colSpan={5} className="p-3 text-xs">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
              <div><span className="text-muted-foreground">Requested model:</span> {row.requested_model || "—"}</div>
              <div><span className="text-muted-foreground">Actual model used:</span> {row.model || "—"}</div>
              <div><span className="text-muted-foreground">Fallback used:</span> {row.fallback_used ? `Yes (primary: ${row.primary_model || "—"})` : "No"}</div>
              <div><span className="text-muted-foreground">Trigger:</span> {row.trigger_event || "—"}</div>
              <div><span className="text-muted-foreground">Opportunity ID:</span> {row.opportunity_id || "—"}</div>
              <div><span className="text-muted-foreground">Contact ID:</span> {row.contact_id || "—"}</div>
              <div><span className="text-muted-foreground">Provider HTTP status:</span> {row.provider_http_status ?? "—"}</div>
              <div><span className="text-muted-foreground">Provider request ID:</span> {row.provider_request_id || "—"}</div>
              <div><span className="text-muted-foreground">Finish reason:</span> {row.finish_reason || "—"}
                {row.finish_reason === "length" && (
                  <span className="text-amber-600"> — the model ran out of its output-token budget before finishing (a likely truncation, not a genuine "insufficient data")</span>
                )}
              </div>
              <div><span className="text-muted-foreground">Reasoning tokens:</span> {row.reasoning_tokens ?? "—"}</div>
              <div><span className="text-muted-foreground">Input / cached / output tokens:</span> {fmtTok(row.prompt_tokens)} / {fmtTok(row.cached_tokens)} / {fmtTok(row.completion_tokens)}</div>
              <div><span className="text-muted-foreground">Test record:</span> {row.is_test_record ? "Yes" : "No"}</div>
            </div>
            <div className="mt-2">
              <span className="text-muted-foreground">Error:</span>{" "}
              <span className="font-mono break-all">{row.error || "—"}</span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const FailedCallsSection = ({ preset, customFrom, customTo }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const { date_from, date_to } = presetToRange(preset, customFrom, customTo);
    const params = { success: false, limit: 100 };
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    api.get("/ai/admin/telemetry/calls", { params }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [preset, customFrom, customTo]);

  return (
    <div className="rounded-md border overflow-x-auto" data-testid="ai-failed-calls-section">
      <p className="text-xs font-medium p-2 border-b bg-muted/30">
        Failed calls (selected range) — click a row for the full diagnostic
      </p>
      <table className="w-full text-xs">
        <thead className="bg-muted/50"><tr>
          <th className="text-left p-2">Function</th>
          <th className="text-left p-2">Auto/manual</th>
          <th className="text-left p-2">Requested model</th>
          <th className="text-left p-2">Failure stage</th>
          <th className="text-left p-2">When</th>
        </tr></thead>
        <tbody>
          {rows === null && <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">Loading…</td></tr>}
          {rows !== null && rows.length === 0 && (
            <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">No failed calls in this range.</td></tr>
          )}
          {(rows || []).map((row) => <FailedCallRow key={row.telemetry_id} row={row} />)}
        </tbody>
      </table>
    </div>
  );
};

const ModelPricingSection = () => {
  const [data, setData] = useState(null);
  const [models, setModels] = useState([]);
  const [form, setForm] = useState({ model: "", input_per_1m: "", cached_input_per_1m: "", output_per_1m: "", effective_from: "" });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = () => {
    api.get("/ai/admin/model-pricing").then((r) => setData(r.data)).catch(() => setData({ current: [], history: [] }));
  };
  useEffect(() => {
    load();
    api.get("/ai/admin/models").then((r) => setModels(r.data.models || [])).catch(() => setModels([]));
  }, []);

  const save = async () => {
    if (!form.model || form.input_per_1m === "" || form.output_per_1m === "") {
      toast.error("Model, input price and output price are required"); return;
    }
    setSaving(true);
    try {
      await api.post("/ai/admin/model-pricing", {
        model: form.model,
        input_per_1m: parseFloat(form.input_per_1m),
        cached_input_per_1m: form.cached_input_per_1m === "" ? null : parseFloat(form.cached_input_per_1m),
        output_per_1m: parseFloat(form.output_per_1m),
        effective_from: form.effective_from || null,
      });
      toast.success(`Pricing saved for ${form.model}`);
      setForm({ model: "", input_per_1m: "", cached_input_per_1m: "", output_per_1m: "", effective_from: "" });
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  if (!data) return <Skeleton className="h-32" />;
  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <p className="text-sm font-medium">Model pricing (per 1M tokens)</p>
        <p className="text-xs text-muted-foreground">
          Provider pricing changes over time — each save creates a new dated version rather than overwriting the old one. A call's own estimated cost always uses whichever price was actually in effect when that call happened, never today's price applied retroactively.
        </p>
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50"><tr>
              <th className="text-left p-2">Model</th><th className="text-right p-2">Input / 1M</th>
              <th className="text-right p-2">Cached input / 1M</th><th className="text-right p-2">Output / 1M</th>
              <th className="text-left p-2">Effective from</th>
            </tr></thead>
            <tbody>
              {(data.current || []).sort((a, b) => a.model.localeCompare(b.model)).map((p) => (
                <tr key={p.pricing_id} className="border-t">
                  <td className="p-2 font-mono">{p.model}</td>
                  <td className="p-2 text-right tabular-nums">${p.input_per_1m}</td>
                  <td className="p-2 text-right tabular-nums">{p.cached_input_per_1m != null ? `$${p.cached_input_per_1m}` : "—"}</td>
                  <td className="p-2 text-right tabular-nums">${p.output_per_1m}</td>
                  <td className="p-2">{fmtIST(p.effective_from)}</td>
                </tr>
              ))}
              {(data.current || []).length === 0 && (
                <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">No pricing configured yet — costs show as "—" until you add a price below.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <button type="button" className="text-[11px] text-muted-foreground underline underline-offset-2" onClick={() => setShowHistory((v) => !v)}>
          {showHistory ? "Hide" : "Show"} full price history ({(data.history || []).length})
        </button>
        {showHistory && (
          <div className="rounded-md border overflow-x-auto max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <tbody>
                {(data.history || []).map((p) => (
                  <tr key={p.pricing_id} className="border-t">
                    <td className="p-2 font-mono">{p.model}</td>
                    <td className="p-2 text-right tabular-nums">${p.input_per_1m} in{p.cached_input_per_1m != null ? ` / $${p.cached_input_per_1m} cached` : ""} / ${p.output_per_1m} out</td>
                    <td className="p-2">{fmtIST(p.effective_from)}</td>
                    <td className="p-2">{p.active ? <Badge className="text-[9px]">current</Badge> : <span className="text-muted-foreground text-[10px]">superseded</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="grid sm:grid-cols-5 gap-2 items-end pt-2 border-t">
          <div className="space-y-1">
            <Label className="text-[10px]">Model</Label>
            <Select value={form.model} onValueChange={(v) => setForm((f) => ({ ...f, model: v }))}>
              <SelectTrigger className="h-8 text-xs" data-testid="pricing-model-select"><SelectValue placeholder="Choose model" /></SelectTrigger>
              <SelectContent>{models.map((m) => <SelectItem key={m.id} value={m.id}>{m.id}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Input $/1M</Label>
            <Input data-testid="pricing-input" className="h-8 text-xs" type="number" step="0.01" value={form.input_per_1m}
              onChange={(e) => setForm((f) => ({ ...f, input_per_1m: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Cached input $/1M (optional)</Label>
            <Input data-testid="pricing-cached-input" className="h-8 text-xs" type="number" step="0.01" value={form.cached_input_per_1m}
              onChange={(e) => setForm((f) => ({ ...f, cached_input_per_1m: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Output $/1M</Label>
            <Input data-testid="pricing-output" className="h-8 text-xs" type="number" step="0.01" value={form.output_per_1m}
              onChange={(e) => setForm((f) => ({ ...f, output_per_1m: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Effective from (blank = now)</Label>
            <Input data-testid="pricing-effective-from" className="h-8 text-xs" type="date" value={form.effective_from}
              onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" disabled={saving} onClick={save} data-testid="pricing-save-button">
            {saving ? "Saving…" : "Save price version"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CostAlertsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ai/admin/cost-alerts").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai/admin/cost-alerts", {
        daily_threshold: cfg.daily_threshold === "" || cfg.daily_threshold == null ? null : parseFloat(cfg.daily_threshold),
        monthly_threshold: cfg.monthly_threshold === "" || cfg.monthly_threshold == null ? null : parseFloat(cfg.monthly_threshold),
      });
      toast.success("Cost alert thresholds saved");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <Skeleton className="h-20" />;
  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <p className="text-sm font-medium">Cost guardrails</p>
        <p className="text-xs text-muted-foreground">Alert only — AI generation is never automatically paused or throttled by these. Leave blank to disable an alert. Checked hourly against the real estimated spend above.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Daily estimated spend threshold ($)</Label>
            <Input data-testid="cost-alert-daily" className="h-8 text-sm" type="number" step="0.01"
              value={cfg.daily_threshold ?? ""} onChange={(e) => setCfg((c) => ({ ...c, daily_threshold: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Monthly estimated spend threshold ($)</Label>
            <Input data-testid="cost-alert-monthly" className="h-8 text-sm" type="number" step="0.01"
              value={cfg.monthly_threshold ?? ""} onChange={(e) => setCfg((c) => ({ ...c, monthly_threshold: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" disabled={saving} onClick={save} data-testid="cost-alert-save-button">
            {saving ? "Saving…" : "Save thresholds"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// FINAL AI ADMIN/COST CHECK (2026-09) — actual OpenAI Organization Usage/
// Costs reconciliation, alongside the existing SalesOS-estimated telemetry
// above. Two clearly separate numbers, never blended: OpenAI's own
// provider-reported spend (requires a separate Admin API key, never the
// regular project key used for real generation calls) vs. SalesOS's own
// per-call estimate. Reconciliation never attempts a per-SalesOS-function
// breakdown of the actual number — OpenAI's API doesn't expose that mapping.
// ---------------------------------------------------------------------------
const OpenAiAdminCredentialsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [projectId, setProjectId] = useState("");
  const [apiKeyId, setApiKeyId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ai/admin/openai-admin-credentials").then((r) => {
    setCfg(r.data);
    setKeyInput(r.data.admin_api_key_masked || "");
    setProjectId(r.data.usage_project_id || "");
    setApiKeyId(r.data.usage_api_key_id || "");
  }).catch(() => setCfg({}));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai/admin/openai-admin-credentials", {
        admin_api_key: keyInput,
        usage_project_id: projectId || "",
        usage_api_key_id: apiKeyId || "",
      });
      toast.success("OpenAI Admin API credentials saved");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <Skeleton className="h-24" />;
  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <p className="text-sm font-medium">OpenAI Admin API credentials</p>
        <p className="text-xs text-muted-foreground">
          A separate, higher-privilege Admin API key (org Settings → Admin Keys on platform.openai.com) — never the
          regular project key already used for real generation calls. Admin-only; never exposed to ordinary
          employees. Used only to read OpenAI's own Organization Usage/Costs endpoints below.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Admin API key {cfg.admin_api_key_configured && <span className="text-emerald-600 dark:text-emerald-400">(configured)</span>}</Label>
            <Input data-testid="openai-admin-key-input" className="h-8 text-sm" type="password"
              placeholder="sk-admin-…" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">SalesOS Project ID (optional filter)</Label>
            <Input data-testid="openai-admin-project-input" className="h-8 text-sm" placeholder="proj_…"
              value={projectId} onChange={(e) => setProjectId(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">SalesOS API Key ID (optional filter)</Label>
            <Input data-testid="openai-admin-apikeyid-input" className="h-8 text-sm" placeholder="key_…"
              value={apiKeyId} onChange={(e) => setApiKeyId(e.target.value)} />
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Setting the Project ID and/or API Key ID scopes the actual-usage numbers below to SalesOS's own OpenAI
          activity, excluding unrelated organization spend where possible.
        </p>
        <div className="flex justify-end">
          <Button size="sm" disabled={saving} onClick={save} data-testid="openai-admin-credentials-save-button">
            {saving ? "Saving…" : "Save credentials"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ActualUsageReconciliationSection = ({ preset, customFrom, customTo, estimatedCost, estimatedCalls }) => {
  const [actual, setActual] = useState(null);

  const load = () => {
    const { date_from, date_to } = presetToRange(preset, customFrom, customTo);
    const params = { date_from: date_from || new Date(0).toISOString(), date_to: date_to || new Date().toISOString() };
    api.get("/ai/admin/telemetry/actual-usage", { params }).then((r) => setActual(r.data)).catch(() => setActual(null));
  };
  useEffect(() => { load(); }, [preset, customFrom, customTo]); // eslint-disable-line react-hooks/exhaustive-deps

  if (actual === null) return <Skeleton className="h-24" />;

  if (!actual.available) {
    return (
      <Card>
        <CardContent className="pt-4">
          <p className="text-sm font-medium mb-1">Actual OpenAI usage/cost</p>
          <p className="text-xs text-muted-foreground">Unavailable — {actual.error}</p>
        </CardContent>
      </Card>
    );
  }

  const diff = actual.total_cost_usd - (estimatedCost ?? 0);
  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <p className="text-sm font-medium">Actual OpenAI usage/cost <span className="text-[10px] font-normal text-muted-foreground">(provider-reported, selected range)</span></p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="rounded-md border p-2 text-center" data-testid="actual-openai-cost">
            <p className="text-lg font-semibold tabular-nums">{fmtMoney(actual.total_cost_usd)}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Actual OpenAI cost</p>
          </div>
          <div className="rounded-md border p-2 text-center" data-testid="estimated-salesos-cost">
            <p className="text-lg font-semibold tabular-nums">{fmtMoney(estimatedCost)}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Estimated SalesOS attribution</p>
          </div>
          <div className="rounded-md border p-2 text-center" data-testid="actual-vs-estimated-diff">
            <p className={`text-lg font-semibold tabular-nums ${diff >= 0 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}>
              {diff >= 0 ? "+" : ""}{fmtMoney(diff)}
            </p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Difference (actual − estimate)</p>
          </div>
          <div className="rounded-md border p-2 text-center">
            <p className="text-lg font-semibold tabular-nums">{fmtTok(actual.total_input_tokens + actual.total_output_tokens)}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Actual total tokens</p>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">
          {actual.project_filter_applied || actual.api_key_filter_applied
            ? "Scoped to the configured SalesOS project/API key."
            : "No project/API key filter configured — this may include unrelated OpenAI organization activity."}
          {" "}The difference reflects the whole organization vs. SalesOS's own per-call estimate — OpenAI's API does
          not expose a per-SalesOS-function breakdown of its own actual cost, so no such breakdown is attempted here.
        </p>
        {Object.keys(actual.by_model || {}).length > 0 && (
          <div className="rounded-md border overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50"><tr>
                <th className="text-left p-2">Model (actual, OpenAI-reported)</th>
                <th className="text-right p-2">Requests</th><th className="text-right p-2">Input tok</th>
                <th className="text-right p-2">Cached tok</th><th className="text-right p-2">Output tok</th>
              </tr></thead>
              <tbody>
                {Object.entries(actual.by_model).map(([m, v]) => (
                  <tr key={m} className="border-t">
                    <td className="p-2">{m}</td>
                    <td className="text-right p-2 tabular-nums">{fmtTok(v.num_requests)}</td>
                    <td className="text-right p-2 tabular-nums">{fmtTok(v.input_tokens)}</td>
                    <td className="text-right p-2 tabular-nums">{fmtTok(v.cached_tokens)}</td>
                    <td className="text-right p-2 tabular-nums">{fmtTok(v.output_tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AiUsageTab = () => {
  const [summary, setSummary] = useState(null);
  const [preset, setPreset] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const load = () => {
    const { date_from, date_to } = presetToRange(preset, customFrom, customTo);
    const params = {};
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    api.get("/ai/admin/telemetry/summary", { params }).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  };
  useEffect(() => { load(); }, [preset, customFrom, customTo]); // eslint-disable-line react-hooks/exhaustive-deps

  if (summary === null) return <p className="text-xs text-muted-foreground">Loading…</p>;
  const ts = summary.top_summary || {};

  return (
    <div className="space-y-4" data-testid="ai-usage-tab">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Estimated SalesOS attribution (per-call telemetry, not OpenAI's own billing)</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          ["Est. cost today", fmtMoney(ts.cost_today)],
          ["Est. this month", fmtMoney(ts.cost_this_month)],
          ["Est. previous month", fmtMoney(ts.cost_previous_month)],
          ["Calls this month", (ts.calls_this_month ?? 0).toLocaleString()],
          ["Tokens this month", fmtTok(ts.tokens_this_month)],
          ["Avg cost / opportunity", fmtMoney(ts.avg_cost_per_opportunity_this_month)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-md border p-2 text-center" data-testid={`ai-usage-summary-${label}`}>
            <p className="text-lg font-semibold tabular-nums">{value}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{label}</p>
          </div>
        ))}
      </div>
      {(summary.models_seen_without_pricing || []).length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          No pricing configured for: {summary.models_seen_without_pricing.join(", ")} — those calls show token counts only. Add a price below for an estimated cost.
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {DATE_PRESETS.map((p) => (
          <Button key={p.key} size="sm" variant={preset === p.key ? "default" : "outline"} className="h-7 text-xs"
            onClick={() => setPreset(p.key)} data-testid={`ai-usage-preset-${p.key}`}>
            {p.label}
          </Button>
        ))}
        {preset === "custom" && (
          <>
            <Input type="date" className="h-7 text-xs w-36" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="ai-usage-custom-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" className="h-7 text-xs w-36" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="ai-usage-custom-to" />
          </>
        )}
        <span className="text-[10px] text-muted-foreground ml-auto">Breakdown below reflects the selected range · Range total (estimated): {fmtMoney(summary.range?.estimated_cost)} · {summary.range?.calls_total ?? 0} calls</span>
      </div>

      <ActualUsageReconciliationSection preset={preset} customFrom={customFrom} customTo={customTo}
        estimatedCost={summary.range?.estimated_cost} estimatedCalls={summary.range?.calls_total} />

      <p className="text-[10px] text-muted-foreground uppercase tracking-wide pt-1">Estimated SalesOS attribution — breakdown by function/model/etc.</p>
      <UsageBucketTable title="By AI function" buckets={summary.by_call_type}
        labelFor={(k) => AI_CALL_TYPE_LABELS[k] || k.replaceAll("_", " ")} />
      <UsageBucketTable title="By model" buckets={summary.by_model} />
      <div className="grid sm:grid-cols-2 gap-3">
        <UsageBucketTable title="Automatic vs manual" buckets={summary.automatic_vs_manual}
          labelFor={(k) => (k === "automatic" ? "Automatic" : "Manual")} />
        <UsageBucketTable title="Successful vs failed" buckets={summary.success_vs_failed}
          labelFor={(k) => (k === "successful" ? "Successful" : "Failed")} />
      </div>
      <UsageBucketTable title="Fallback calls" buckets={{ fallback: summary.fallback_used }}
        labelFor={() => "Calls that used a configured fallback model"} />

      <FailedCallsSection preset={preset} customFrom={customFrom} customTo={customTo} />

      <OpenAiAdminCredentialsSection />
      <ModelPricingSection />
      <CostAlertsSection />
    </div>
  );
};

// ---------------------------------------------------------------------------
// AI Runtime & Automatic Refresh (2026-09) — spec sections 5/6: business-
// readable automatic-refresh controls (master switch + per-event toggles +
// debounce interval) and employee manual-regeneration cost controls
// (cooldown/quota, manager+ implicitly unlimited). Deliberately does NOT
// expose every low-level TIMELINE_EVENT_TYPES hook as its own toggle — only
// the categories with real, standalone cost-control value, matching
// ai_orchestration.py's own _TOGGLE_GATED_EVENTS.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// FINAL AI ADMIN/COST CHECK (2026-09) — Unified AI Admin Control: one
// compact view over every AI automation already present, reusing the
// existing scheduler/ai_call_telemetry data rather than a second automation
// framework. See routers_ai_admin.py's AUTOMATION_DEFS for the source of
// truth this table renders.
// ---------------------------------------------------------------------------
const AiAutomationsSection = () => {
  const [rows, setRows] = useState(null);
  const [runningKey, setRunningKey] = useState(null);

  const load = () => api.get("/ai/admin/automations").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    try {
      await api.put(`/ai/admin/automations/${key}/toggle`, null, { params: { enabled } });
      toast.success(enabled ? "Automation enabled" : "Automation disabled");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const runNow = async (key) => {
    setRunningKey(key);
    try {
      const r = await api.post(`/ai/admin/automations/${key}/run-now`);
      toast.success(`Run complete: ${JSON.stringify(r.data.result).slice(0, 200)}`);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setRunningKey(null); }
  };

  if (!rows) return <Skeleton className="h-40" />;

  return (
    <Card data-testid="ai-automations-section">
      <CardContent className="pt-4 space-y-3">
        <p className="text-sm font-medium flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" />Unified AI Admin Control</p>
        <p className="text-xs text-muted-foreground max-w-2xl">Every AI automation in one place — enabled state, trigger/cadence, last and next run, last result, and usage over the last 30 days. Reuses the same scheduler/runtime settings above; this is a view over them, not a second automation system.</p>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.key} className="rounded-md border p-3 space-y-2" data-testid={`automation-row-${r.key}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm font-medium">{r.label}</p>
                  <p className="text-[11px] text-muted-foreground">{r.cadence}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={r.enabled ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground"}>
                    {r.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                  {r.toggleable && (
                    <Switch data-testid={`automation-toggle-${r.key}`} checked={!!r.enabled}
                      onCheckedChange={(v) => toggle(r.key, v)} />
                  )}
                  {r.run_now_supported && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={runningKey === r.key}
                      onClick={() => runNow(r.key)} data-testid={`automation-run-now-${r.key}`}>
                      <Play className="h-3 w-3" />{runningKey === r.key ? "Running…" : "Run Now"}
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div><span className="text-muted-foreground">Last run: </span>{r.last_run_at ? fmtIST(r.last_run_at) : "—"}</div>
                <div><span className="text-muted-foreground">Next run: </span>{r.next_run_at ? fmtIST(r.next_run_at) : "—"}</div>
                <div><span className="text-muted-foreground">Last status: </span>{r.last_status || "—"}</div>
                <div><span className="text-muted-foreground">Calls (30d): </span>{r.usage_last_30d?.calls_total ?? 0}</div>
              </div>
              {r.last_result && <p className="text-[10px] text-muted-foreground truncate" title={r.last_result}>Last result: {r.last_result}</p>}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] text-muted-foreground">
                <div>Tokens (30d): {fmtTok(r.usage_last_30d?.total_tokens)}</div>
                <div>Est. cost (30d): {fmtMoney(r.usage_last_30d?.estimated_cost)}</div>
                <div>Failed (30d): {r.usage_last_30d?.calls_failed ?? 0}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const AiRuntimeTab = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [eventsDraft, setEventsDraft] = useState({});

  const load = () => {
    api.get("/ai/admin/runtime-config").then((r) => {
      setCfg(r.data);
      setEventsDraft(r.data.auto_refresh?.events || {});
    }).catch((e) => toast.error(errDetail(e)));
  };
  useEffect(() => { load(); }, []);

  const patch = async (body, successMsg) => {
    setSaving(true);
    try {
      await api.put("/ai/admin/runtime-config", body);
      toast.success(successMsg || "Saved");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-32" />)}</div>;
  const ar = cfg.auto_refresh || {};
  const mr = cfg.manual_refresh || {};

  return (
    <div className="space-y-4" data-testid="ai-runtime-tab">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" />Automatic AI refresh</p>
              <p className="text-xs text-muted-foreground max-w-2xl">Keeps V2 scoring and the Sales Mentor briefing current as real evidence arrives, without an employee having to click Generate. Always runs V2 first, then Mentor — a Mentor briefing can never accidentally compare itself against a stale V2 snapshot.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">Master Auto Refresh</span>
              <Switch data-testid="auto-refresh-master-toggle" checked={!!ar.master_enabled}
                onCheckedChange={(v) => patch({ auto_refresh_master_enabled: v }, v ? "Automatic AI refresh enabled" : "Automatic AI refresh disabled")} />
            </div>
          </div>
          <div className={ar.master_enabled ? "" : "opacity-50 pointer-events-none"}>
            <p className="text-xs font-medium mb-1.5">Trigger on</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(ar.event_labels || {}).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between rounded border px-2.5 py-1.5">
                  <span className="text-xs">{label}</span>
                  <Switch data-testid={`auto-refresh-event-${key}`}
                    checked={eventsDraft[key] !== false}
                    onCheckedChange={(v) => {
                      const next = { ...eventsDraft, [key]: v };
                      setEventsDraft(next);
                      patch({ auto_refresh_events: next });
                    }} />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">A new lifecycle-closure recompute and outcome-bearing task completions always stay on while the master switch is on — they're already narrowly scoped to real evidence, not a separate cost lever.</p>
            <div className="flex items-center gap-2 mt-2">
              <Label className="text-xs shrink-0">Debounce (seconds)</Label>
              <Input data-testid="auto-refresh-debounce-input" type="number" min="0" className="h-7 text-xs w-24"
                defaultValue={ar.debounce_seconds} disabled={saving}
                onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) patch({ auto_refresh_debounce_seconds: v }); }} />
              <span className="text-[11px] text-muted-foreground">Several changes to the same opportunity within this window coalesce into one refresh, not several back-to-back calls.</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Employee manual regeneration</p>
              <p className="text-xs text-muted-foreground max-w-2xl">With automatic refresh on, employees should rarely need this — it stays available as manual recovery. Manager/supervisor/admin are never rate-limited (force-refresh capability). Automatic system refreshes never consume an employee's manual quota.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground">Allow manual refresh</span>
              <Switch data-testid="manual-refresh-enabled-toggle" checked={!!mr.enabled}
                onCheckedChange={(v) => patch({ manual_refresh_enabled: v })} />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Cooldown between refreshes (seconds)</Label>
              <Input data-testid="manual-refresh-cooldown-input" type="number" min="0" className="h-8 text-sm"
                defaultValue={mr.cooldown_seconds}
                onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) patch({ manual_refresh_cooldown_seconds: v }); }} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max per opportunity / day</Label>
              <Input data-testid="manual-refresh-max-opp-input" type="number" min="1" className="h-8 text-sm"
                defaultValue={mr.max_per_opportunity_per_day}
                onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) patch({ manual_refresh_max_per_opportunity_per_day: v }); }} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max per employee / day</Label>
              <Input data-testid="manual-refresh-max-user-input" type="number" min="1" className="h-8 text-sm"
                defaultValue={mr.max_per_employee_per_day}
                onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) patch({ manual_refresh_max_per_employee_per_day: v }); }} />
            </div>
          </div>
        </CardContent>
      </Card>

      <AiAutomationsSection />
    </div>
  );
};

// ---------------------------------------------------------------------------
export const AiIntelligenceTab = () => {
  const { hasCap } = useAuth();
  if (!hasCap("ai.config.view")) {
    return <EmptyState icon={Sparkles} title="Not authorised" hint="AI Intelligence configuration requires manager or admin access." />;
  }
  const canReviewLearning = hasCap("ai.learning.review");
  const canManageRuntime = hasCap("admin.config");
  return (
    <div className="space-y-3" data-testid="ai-intelligence-admin">
      <p className="text-sm text-muted-foreground">Global Rules, per-product AI profiles, Consultant Intelligence weights, a safe test/preview facility, and (Phase 5) the continuous-learning proposal review queue + AI usage dashboard. Every change is Draft/Published-versioned or human-approved — nothing here ever reaches production silently.</p>
      <Tabs defaultValue="global">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="ai-admin-tab-global" value="global">Global Rules</TabsTrigger>
          <TabsTrigger data-testid="ai-admin-tab-products" value="products">Product Profiles</TabsTrigger>
          <TabsTrigger data-testid="ai-admin-tab-consultant" value="consultant">Consultant Intelligence</TabsTrigger>
          <TabsTrigger data-testid="ai-admin-tab-test" value="test">Test & Version History</TabsTrigger>
          <TabsTrigger data-testid="ai-admin-tab-corrections" value="corrections">Corrections</TabsTrigger>
          {canReviewLearning && (
            <TabsTrigger data-testid="ai-admin-tab-learning" value="learning" className="gap-1.5">
              <GraduationCap className="h-3.5 w-3.5" />Learning Proposals
            </TabsTrigger>
          )}
          {canReviewLearning && (
            <TabsTrigger data-testid="ai-admin-tab-usage" value="usage" className="gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" />AI Usage
            </TabsTrigger>
          )}
          {canReviewLearning && (
            <TabsTrigger data-testid="ai-admin-tab-memory-export" value="memory-export" className="gap-1.5">
              <Download className="h-3.5 w-3.5" />Memory Export
            </TabsTrigger>
          )}
          {canManageRuntime && (
            <TabsTrigger data-testid="ai-admin-tab-runtime" value="runtime" className="gap-1.5">
              <Zap className="h-3.5 w-3.5" />AI Runtime
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="global" className="pt-3"><GlobalRulesTab /></TabsContent>
        <TabsContent value="products" className="pt-3"><ProductProfilesTab /></TabsContent>
        <TabsContent value="corrections" className="pt-3"><CorrectionsTab /></TabsContent>
        <TabsContent value="consultant" className="pt-3"><ConsultantIntelligenceTab /></TabsContent>
        <TabsContent value="test" className="pt-3"><TestPreviewTab /></TabsContent>
        {canReviewLearning && <TabsContent value="learning" className="pt-3"><LearningProposalsTab /></TabsContent>}
        {canReviewLearning && <TabsContent value="usage" className="pt-3"><AiUsageTab /></TabsContent>}
        {canReviewLearning && <TabsContent value="memory-export" className="pt-3"><MemoryExportTab /></TabsContent>}
        {canManageRuntime && <TabsContent value="runtime" className="pt-3"><AiRuntimeTab /></TabsContent>}
      </Tabs>
    </div>
  );
};
